/**
 * Reusable "Enable/Disable" row for a single login provider. Pairs with the
 * super-admin `GET/PUT /admin/auth-toggles` endpoint and instantly hides the
 * button on /login + /signup when flipped OFF.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export type AuthProviderKey = "google" | "facebook" | "linkedin" | "passkey";

export interface AuthTogglesStatus {
  google: boolean;
  facebook: boolean;
  linkedin: boolean;
  passkey: boolean;
}

export const useAuthToggles = () =>
  useQuery<AuthTogglesStatus>({
    queryKey: ["auth-toggles"],
    queryFn: async () => (await api.get("/admin/auth-toggles")).data,
  });

interface Props {
  provider: AuthProviderKey;
  label: string;
  disabled?: boolean;
}

export const AuthProviderToggleRow: React.FC<Props> = ({ provider, label, disabled }) => {
  const qc = useQueryClient();
  const toggles = useAuthToggles();
  const enabled = !!toggles.data?.[provider];

  const mut = useMutation({
    mutationFn: async (next: boolean) =>
      (await api.put("/admin/auth-toggles", { provider, enabled: next })).data,
    onSuccess: (_d, next) => {
      toast.success(`${label} ${next ? "enabled" : "disabled"}`);
      qc.invalidateQueries({ queryKey: ["auth-toggles"] });
      // Public config endpoints drive the login-page buttons — refresh those too.
      qc.invalidateQueries({ queryKey: ["google-signin-config"] });
      qc.invalidateQueries({ queryKey: ["fb-login-config"] });
      qc.invalidateQueries({ queryKey: ["linkedin-signin-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const testId = `auth-toggle-${provider}`;
  return (
    <div
      className="flex items-center justify-between gap-3 mb-4 rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-3 py-2.5"
      data-testid={`${testId}-row`}
    >
      <div className="min-w-0">
        <Label htmlFor={testId} className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-fg)]">
          {label}
        </Label>
        <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">
          {enabled
            ? "Enabled — the button shows on /login and /signup."
            : "Disabled — the button is hidden and requests are rejected."}
        </div>
      </div>
      <Switch
        id={testId}
        data-testid={testId}
        checked={enabled}
        disabled={!!disabled || toggles.isLoading || mut.isPending}
        onCheckedChange={(v) => mut.mutate(!!v)}
      />
    </div>
  );
};

export default AuthProviderToggleRow;
